import { Flex, Spacing, Text } from '@billy/design-system'
import { type ReactElement, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { LIVE_PREVIEW_DEBOUNCE_TIME } from '@features/invoices/constants/livePreviewDebounceTime'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'

import { useDebouncedValue } from '../../../../../../hooks/useDebouncedValue'
import { useForm } from '../../../../../../hooks/useForm'
import { DocumentTypeSettingsFormFields } from '../../../../elements/DocumentTypeSettings/elements/DocumentTypeSettingsFormFields'
import { usePredefinedDocumentTypeSettingsFormValues } from '../../../../elements/DocumentTypeSettings/hooks/usePredefinedDocumentTypeSettingsFormValues'
import type { DocumentTypeSettingsFormSchema } from '../../../../elements/DocumentTypeSettings/types/documentTypeSettingsFormSchema'
import { getDocumentTypeSettingsFieldPatch } from '../../../../elements/DocumentTypeSettings/utils/getDocumentTypeSettingsFieldPatch'
import { getDocumentTypeSettingsFormSchema } from '../../../../elements/DocumentTypeSettings/utils/getDocumentTypeSettingsFormSchema'
import { getDocumentTypeSettingsFormValues } from '../../../../elements/DocumentTypeSettings/utils/getDocumentTypeSettingsFormValues'
import { InvoicingSettingsSection } from '../../../../enums/invoicingSettingsSection'
import { useRegisterInvoicingSettingsForm } from '../../../../hooks/useRegisterInvoicingSettingsForm'
import { useRegisterInvoicingSettingsPreviewPatch } from '../../../../hooks/useRegisterInvoicingSettingsPreviewPatch'
import type {
  InvoicingSettingsFormSaveOptions,
  InvoicingSettingsFormSaveResult,
} from '../../../../types/invoicingSettingsFormSave'
import type { InvoicingSettingsPreviewPatch } from '../../../../types/invoicingSettingsPreviewPatch'

export const InvoiceSettingsForm = (): ReactElement => {
  const { t } = useTranslation()
  const {
    defaultTexts,
    hasFailedToLoad,
    isLoading,
    predefinedDocumentTypeSettingsFormValues,
    refetch,
    savedColumnNames,
  } = usePredefinedDocumentTypeSettingsFormValues(InvoiceDocumentType.Invoice)
  const { Form, formState, getValues, handleSubmit, reset, watch } = useForm<DocumentTypeSettingsFormSchema>({
    defaultValues: getDocumentTypeSettingsFormValues({}),
    validationSchema: getDocumentTypeSettingsFormSchema(),
  })

  const { isDirty } = formState
  const hasLoadedSettings = !isLoading && !hasFailedToLoad
  const formValues = watch()
  const { footerNote, message, termsAndConditions } = formValues
  // Memoized on the strings: a new object on every render would restart the debounce forever.
  const textValues = useMemo(
    () => ({ footerNote, message, termsAndConditions }),
    [footerNote, message, termsAndConditions],
  )
  // Undefined until the loaded texts settle, so the empty pre-load defaults never reach the preview.
  const debouncedTextValues = useDebouncedValue(hasLoadedSettings ? textValues : undefined, LIVE_PREVIEW_DEBOUNCE_TIME)

  const handleFormReset = useCallback(() => {
    reset(getValues())
  }, [getValues, reset])

  const handleSave = useCallback(
    ({ updateSettings }: InvoicingSettingsFormSaveOptions): Promise<InvoicingSettingsFormSaveResult> => {
      if (!hasLoadedSettings) {
        return Promise.reject(new Error('Invoice settings are not loaded'))
      }

      return new Promise((resolve, reject) => {
        handleSubmit(
          async (values) => {
            const fieldPatch = getDocumentTypeSettingsFieldPatch({
              defaultTexts,
              savedColumnNames,
              savedValues: predefinedDocumentTypeSettingsFormValues,
              values,
            })

            if (Object.keys(fieldPatch).length > 0) {
              await updateSettings({ modelPatches: { [InvoiceDocumentType.Invoice]: fieldPatch } })
            }

            resolve('saved')
          },
          () => {
            resolve('invalid')
          },
        )().catch(reject)
      })
    },
    [defaultTexts, handleSubmit, hasLoadedSettings, predefinedDocumentTypeSettingsFormValues, savedColumnNames],
  )

  useRegisterInvoicingSettingsForm({ isDirty, resetForm: handleFormReset, save: handleSave })

  // Texts reach the preview debounced, every other field at once.
  const previewTextValues = debouncedTextValues ?? {
    footerNote: predefinedDocumentTypeSettingsFormValues.footerNote,
    message: predefinedDocumentTypeSettingsFormValues.message,
    termsAndConditions: predefinedDocumentTypeSettingsFormValues.termsAndConditions,
  }
  const previewFieldPatch =
    isDirty && hasLoadedSettings
      ? getDocumentTypeSettingsFieldPatch({
          defaultTexts,
          savedColumnNames,
          savedValues: predefinedDocumentTypeSettingsFormValues,
          values: { ...formValues, ...previewTextValues },
        })
      : {}

  const previewPatch: InvoicingSettingsPreviewPatch | undefined =
    Object.keys(previewFieldPatch).length > 0
      ? { settings: { modelPatches: { [InvoiceDocumentType.Invoice]: previewFieldPatch } } }
      : undefined

  useRegisterInvoicingSettingsPreviewPatch(previewPatch)

  // Replace the empty defaults with server values once the settings document has loaded.
  // Skip this while the user has unsaved edits, so a refetch cannot wipe them.
  useEffect(() => {
    if (!hasLoadedSettings || isDirty) {
      return
    }

    reset(predefinedDocumentTypeSettingsFormValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isDirty is a guard, not a dependency: re-running when the form turns clean after save would reset over the values the host just kept
  }, [hasLoadedSettings, predefinedDocumentTypeSettingsFormValues, reset])

  return (
    <Flex flexDirection="column" gap={Spacing.S24}>
      <Text variant="h2">{t('invoices')}</Text>
      <Form>
        <DocumentTypeSettingsFormFields
          hasFailedToLoad={hasFailedToLoad}
          isLoading={isLoading}
          onRetry={refetch}
          trackingSection={InvoicingSettingsSection.Invoice}
        />
      </Form>
    </Flex>
  )
}
